import { Line } from '@ant-design/plots';
import { PageContainer } from '@ant-design/pro-components';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  App,
  Card,
  Col,
  DatePicker,
  Empty,
  Flex,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { CSSGrid } from '@/common/components';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { useQueryParams } from '@/common/hooks/use-query-params';
import PropTypes from 'prop-types';
import AnalysisAccountMetricCard from '../components/AnalysisAccountMetricCard';
import AnalysisContentTrendChart from '../components/AnalysisContentTrendChart';
import AnalysisMetricCard from '../components/AnalysisMetricCard';
import AnalysisTopContentTable from '../components/AnalysisTopContentTable';
import HotRadarDetailDrawer from '../components/HotRadarDetailDrawer';
import { queryTraceAnalysis, queryTraceContentTrend } from '../services/analysis';
import { createHotRadarFromContentLog, queryHotRadars } from '../services/hot-radars';
import { formatTraceAccountName } from '../utils/platform';
import {
  buildTrendIndexSeries,
  formatAnalysisDate,
  filterMetricItemsByPlatform,
  formatTrendPeriodTitle,
  getDefaultAnalysisRange,
  getMetricOptionsForAccounts,
  getMetricTitle,
  getTrendIndexTooltipItem,
  getTrendTimeValue,
  metricOptions,
  periodOptions,
} from '../utils/analysis';

const { RangePicker } = DatePicker;

const dateFormat = 'YYYY-MM-DD';
const defaultPeriod = 'custom';

const trendTooltipCss = {
  '.g2-tooltip': {
    'max-width': 'min(520px, calc(100vw - 32px))',
    'min-width': 'min(360px, calc(100vw - 32px))',
  },
  '.g2-tooltip-list-item-value': {
    flex: 'none',
    overflow: 'visible',
    'text-overflow': 'clip',
  },
};

const getQueryValue = (value) => (Array.isArray(value) ? value.at(-1) : value);

const getQueryList = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => String(item).split(',')).filter(Boolean);
  }
  return String(value || '')
    .split(',')
    .filter(Boolean);
};

const getDateText = (value) => value.format(dateFormat);

const formatTrendTooltipTitle = (granularity) => (item) =>
  formatTrendPeriodTitle(item?.dateValue ?? item, granularity);

const getLocalDayjs = (date) =>
  dayjs(new Date(date.getFullYear(), date.getMonth(), date.getDate()));

const getWeekStart = (today) => {
  const todayDate = today.toDate();
  const weekday = todayDate.getDay() || 7;
  const startDate = new Date(todayDate);
  startDate.setDate(todayDate.getDate() + 1 - weekday);
  return getLocalDayjs(startDate);
};

const getPeriodRange = (period) => {
  const today = dayjs();
  if (period === 'month') {
    return [today.startOf('month'), today.endOf('day')];
  }
  if (period === 'quarter') {
    return [today.month(Math.floor(today.month() / 3) * 3).startOf('month'), today.endOf('day')];
  }
  if (period === 'year') {
    return [today.startOf('year'), today.endOf('day')];
  }
  return [getWeekStart(today), today.endOf('day')];
};

const getRangePickerValue = ({ end, period, start, useDefaultRange }) => {
  const startDate = typeof start === 'string' && start ? dayjs(start) : null;
  const endDate = typeof end === 'string' && end ? dayjs(end) : null;
  if (startDate?.isValid() && endDate?.isValid()) {
    return [startDate.startOf('day'), endDate.endOf('day')];
  }
  if (useDefaultRange) {
    return getDefaultAnalysisRange();
  }
  return getPeriodRange(period);
};

const Analysis = ({
  accountType = 'own',
  emptyAccountDescription = '暂无自有账号数据',
  showSummary = true,
  title = '运营看板',
}) => {
  const { message } = App.useApp();
  const [hotRadarRecord, setHotRadarRecord] = useState(null);
  const [trendContent, setTrendContent] = useState(null);
  const { organizationId } = useOrganizationPath();
  const [query, setQuery] = useQueryParams();
  const requestedMetric = getQueryValue(query.metric);
  const metric = metricOptions.some((item) => item.key === requestedMetric)
    ? requestedMetric
    : 'followerCount';
  const requestedPeriod = getQueryValue(query.period);
  const period = requestedPeriod || defaultPeriod;
  const start = getQueryValue(query.start);
  const end = getQueryValue(query.end);
  const accountIds = getQueryList(query.accountIds);
  const rangePickerValue = getRangePickerValue({
    end,
    period,
    start,
    useDefaultRange: !requestedPeriod && !start && !end,
  });
  const activeRange = {
    end: getDateText(rangePickerValue[1]),
    start: getDateText(rangePickerValue[0]),
  };

  const {
    data = {},
    isFetching,
    isPending,
  } = useQuery({
    enabled: Boolean(organizationId),
    placeholderData: (previousData) => previousData,
    queryFn: () =>
      queryTraceAnalysis({
        accountIds,
        accountType,
        end: activeRange.end,
        metric,
        organizationId,
        period,
        start: activeRange.start,
      }),
    queryKey: [
      'trace-analysis',
      accountType,
      organizationId,
      metric,
      period,
      activeRange.start,
      activeRange.end,
      accountIds.join(','),
    ],
  });
  const initialLoading = isPending;
  const trendLoading = isFetching && !isPending;
  const availableRange = data.filters?.availableRange || {};
  const minAvailableDate = dayjs(availableRange.min);
  const maxAvailableDate = dayjs(availableRange.max);
  const hasMinAvailableDate = minAvailableDate.isValid();
  const hasMaxAvailableDate = maxAvailableDate.isValid();
  const trendSeries = buildTrendIndexSeries(data.trend?.series).map((item) => ({
    ...item,
    dateValue: getTrendTimeValue(item.date),
    seriesName: formatTraceAccountName(item),
  }));
  const { data: contentTrend = {}, isFetching: contentTrendLoading } = useQuery({
    enabled: Boolean(
      organizationId &&
      trendContent?.platform &&
      trendContent?.accountId &&
      trendContent?.contentId,
    ),
    queryFn: () =>
      queryTraceContentTrend({
        accountId: trendContent.accountId,
        accountType,
        contentId: trendContent.contentId,
        organizationId,
        platform: trendContent.platform,
      }),
    queryKey: [
      'trace-content-trend',
      accountType,
      organizationId,
      trendContent?.platform,
      trendContent?.accountId,
      trendContent?.contentId,
    ],
  });

  const { mutateAsync: createHotRadar, isPending: creatingHotRadar } = useMutation({
    mutationFn: createHotRadarFromContentLog,
    onSuccess: () => message.success('爆款拆解任务已提交'),
    onError: () => message.error('爆款拆解生成失败，请重试'),
  });

  const { mutateAsync: findHotRadar, isPending: findingHotRadar } = useMutation({
    mutationFn: (content) =>
      queryHotRadars({
        contentId: content.contentId,
        current: 1,
        organizationId,
        pageSize: 1,
      }),
    onError: () => message.error('爆款拆解查询失败，请重试'),
  });

  const selectPeriod = (value) => {
    const [periodStart, periodEnd] = getPeriodRange(value);
    setQuery({
      end: getDateText(periodEnd),
      period: value,
      start: getDateText(periodStart),
    });
  };

  const selectRange = (value) => {
    if (!value?.[0] || !value?.[1]) {
      const [periodStart, periodEnd] = getDefaultAnalysisRange();
      setQuery({
        end: getDateText(periodEnd),
        period: defaultPeriod,
        start: getDateText(periodStart),
      });
      return;
    }
    if (Math.abs(value[1].diff(value[0], 'day')) > 366) {
      message.error(`${title}最多支持查询 1 年数据`);
      return;
    }
    if (
      (hasMinAvailableDate && value[0].isBefore(minAvailableDate, 'day')) ||
      (hasMaxAvailableDate && value[1].isAfter(maxAvailableDate, 'day'))
    ) {
      message.error('请选择有数据的时间范围');
      return;
    }
    setQuery({
      end: value[1].format(dateFormat),
      period: 'custom',
      start: value[0].format(dateFormat),
    });
  };

  const selectMetric = (key) => {
    setQuery({
      metric: key,
    });
  };

  const selectAccounts = (values) => {
    setQuery({
      accountIds: values.length > 0 ? values.join(',') : undefined,
    });
  };

  const handleHotRadarClick = async (content) => {
    const result = await findHotRadar(content);
    const record = result.data?.[0];
    if (record) {
      setHotRadarRecord(record);
      return;
    }
    await createHotRadar({ id: content.id, organizationId });
  };

  const accountOptions = (data.filters?.accounts || []).map((account) => ({
    accountId: account.accountId,
    label: formatTraceAccountName(account),
    name: account.name,
    platform: account.platform,
    value: account.accountKey,
  }));
  const activeAccounts =
    accountIds.length > 0
      ? accountOptions.filter((account) => accountIds.includes(account.value))
      : accountOptions;
  const availableMetricOptions = getMetricOptionsForAccounts(activeAccounts);
  const currentMetric = availableMetricOptions.some((item) => item.key === metric)
    ? metric
    : availableMetricOptions[0]?.key || 'followerCount';
  const visibleSummary = filterMetricItemsByPlatform(
    data.summary || [],
    activeAccounts.length === 1 ? activeAccounts[0].platform : undefined,
  ).filter((item) => availableMetricOptions.some((option) => option.key === item.key));

  const trendTitle = `${getMetricTitle(currentMetric)}趋势指数`;

  useEffect(() => {
    if (currentMetric !== metric) {
      setQuery({ metric: currentMetric });
    }
  }, [currentMetric, metric, setQuery]);

  const disabledAnalysisDate = (current, info) => {
    if (!current) {
      return false;
    }
    if (hasMinAvailableDate && current.isBefore(minAvailableDate, 'day')) {
      return true;
    }
    if (hasMaxAvailableDate && current.isAfter(maxAvailableDate, 'day')) {
      return true;
    }
    return info?.from ? Math.abs(current.diff(info.from, 'day')) > 366 : false;
  };

  return (
    <PageContainer>
      <Flex vertical gap="large">
        {showSummary ? (
          <Card loading={initialLoading} variant="borderless" title="总量统计">
            <CSSGrid columns={{ xs: 1, sm: 2, xl: 4 }} gap="large">
              {visibleSummary.map((item) => (
                <AnalysisMetricCard key={item.key} item={item} />
              ))}
            </CSSGrid>
          </Card>
        ) : null}

        <Card loading={initialLoading} variant="borderless" title="账号统计">
          <CSSGrid columns={{ xs: 1, lg: 2, xl: 3, xxl: 4 }} gap="large">
            {(data.accounts || []).map((account) => (
              <AnalysisAccountMetricCard key={account.accountKey} account={account} />
            ))}
            {!initialLoading && (data.accounts || []).length === 0 ? (
              <Empty description={emptyAccountDescription} />
            ) : null}
          </CSSGrid>
        </Card>

        <Card
          loading={initialLoading}
          variant="borderless"
          title="趋势分析"
          extra={
            <Select
              mode="multiple"
              allowClear
              value={accountIds}
              onChange={selectAccounts}
              options={accountOptions}
              maxTagCount="responsive"
              styles={{ root: { maxWidth: '50vw', width: '240px' } }}
              placeholder="全部账号"
            />
          }
        >
          <Flex vertical gap="middle">
            <Flex justify="space-between" gap="middle" wrap>
              <Space wrap>
                <Segmented
                  value={currentMetric}
                  onChange={selectMetric}
                  options={availableMetricOptions.map((item) => ({
                    label: item.label,
                    value: item.key,
                  }))}
                />
                <Segmented
                  value={period === 'custom' ? '' : period}
                  onChange={selectPeriod}
                  options={periodOptions}
                />
              </Space>
              <RangePicker
                value={rangePickerValue}
                onChange={selectRange}
                disabledDate={disabledAnalysisDate}
                variant="filled"
              />
            </Flex>
            <Row gutter={[24, 24]} align="top">
              <Col xxl={18} xl={16} lg={24}>
                <Card
                  loading={trendLoading}
                  size="small"
                  type="inner"
                  title={trendTitle}
                  styles={{ body: { height: '478px' } }}
                >
                  <Line
                    height={425}
                    data={trendSeries}
                    xField="dateValue"
                    yField="trendIndex"
                    colorField="seriesName"
                    shapeField="smooth"
                    point={{ sizeField: 2 }}
                    axis={{
                      x: {
                        title: false,
                        labelFormatter: formatAnalysisDate,
                      },
                      y: { title: false },
                    }}
                    legend={{
                      color: {
                        layout: { justifyContent: 'center' },
                        position: 'bottom',
                      },
                    }}
                    scale={{
                      x: { type: 'time' },
                    }}
                    interaction={{
                      tooltip: {
                        css: trendTooltipCss,
                        shared: true,
                      },
                    }}
                    tooltip={{
                      items: [getTrendIndexTooltipItem],
                      title: formatTrendTooltipTitle(data.trend?.granularity),
                    }}
                  />
                </Card>
              </Col>
              <Col xxl={6} xl={8} lg={24}>
                <Card
                  loading={trendLoading}
                  size="small"
                  type="inner"
                  title="TOP 7 作品"
                  styles={{ body: { height: '478px' } }}
                >
                  <AnalysisTopContentTable
                    dataSource={data.trend?.topContents || []}
                    loading={findingHotRadar || creatingHotRadar}
                    metric={currentMetric}
                    onHotRadarClick={handleHotRadarClick}
                    onTrendClick={setTrendContent}
                  />
                </Card>
              </Col>
            </Row>
          </Flex>
        </Card>
      </Flex>
      <Modal
        destroyOnHidden
        footer={null}
        loading={contentTrendLoading}
        onCancel={() => setTrendContent(null)}
        open={Boolean(trendContent)}
        styles={{ body: { height: 478 } }}
        title={
          <Typography.Text ellipsis={{ tooltip: trendContent?.title }}>
            {trendContent?.title}
          </Typography.Text>
        }
        width={1088}
      >
        <AnalysisContentTrendChart dataSource={contentTrend.series || []} />
      </Modal>
      <HotRadarDetailDrawer
        record={hotRadarRecord}
        open={Boolean(hotRadarRecord)}
        onClose={() => setHotRadarRecord(null)}
      />
    </PageContainer>
  );
};

export default Analysis;

Analysis.propTypes = {
  accountType: PropTypes.oneOf(['own', 'rival']),
  emptyAccountDescription: PropTypes.string,
  showSummary: PropTypes.bool,
  title: PropTypes.string,
};
